import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Heart, Plus, Trash2, Check, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Public, no-auth intake form for prospective clients — a salon shares
// one link (e.g. on Instagram bio or in an email) and anyone can submit
// a blank intake. Each submission creates a new "inquiry" event in the
// salon's workspace, which they can review and book from there.
export default function PublicOpenIntakePage() {
  const { slug } = useParams();
  const [salon, setSalon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const todayPlus = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  const [form, setForm] = useState({
    type: "wedding",
    name: "",
    date: todayPlus(60),
    time: "10:00",
    venue: "",
    party_size: 0,
    lead_client: { name: "", phone: "", email: "" },
    roster: [],
    notes: "",
    inspo_link: "",
    dress_color: "",
    allergies: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/events/open-intake/${slug}`);
        if (!cancelled) setSalon(res.data);
      } catch {
        if (!cancelled) setError("This link doesn't match a salon. Double-check it with whoever sent it.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const setLead = (k, v) => setForm(f => ({ ...f, lead_client: { ...f.lead_client, [k]: v } }));
  const updateRoster = (idx, k, v) => {
    const next = [...form.roster];
    next[idx] = { ...next[idx], [k]: v };
    setForm(f => ({ ...f, roster: next }));
  };
  const addRow = () => setForm(f => ({ ...f, roster: [...f.roster, { name: "", service: "", start_time: "" }] }));
  const removeRow = (idx) => setForm(f => ({ ...f, roster: f.roster.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name?.trim()) { setError("Event name is required"); return; }
    if (!form.lead_client?.name?.trim()) { setError("Please enter your name"); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/public/events/open-intake/${slug}`, {
        ...form,
        party_size: parseInt(form.party_size || 0, 10) || (form.roster?.length || 0),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submit failed. Please try again.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !salon) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50 px-4">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-10 text-center">
          <p className="font-['Outfit'] text-2xl font-semibold text-slate-900 mb-2">Salon not found</p>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50 px-4" data-testid="open-intake-submitted">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500 flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30">
            <Check className="w-8 h-8 text-white" strokeWidth={3} />
          </div>
          <h2 className="font-['Outfit'] text-3xl font-semibold text-slate-900 mb-2">Thank you!</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Your event request has been sent to <span className="font-semibold">{salon?.business_name || "the salon"}</span>. They'll reach out within 24 hours to confirm and discuss pricing.
          </p>
          <p className="text-xs text-slate-400 mt-4">You can close this page now.</p>
        </div>
      </div>
    );
  }

  const businessName = salon?.business_name || "Our salon";

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 py-10 px-4" data-testid="open-intake-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 flex items-center justify-center mb-4 shadow-lg shadow-rose-500/30">
            <Heart className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-rose-500 mb-1">{businessName}</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Event inquiry form
          </h1>
          <p className="text-slate-600 text-sm mt-2 max-w-md mx-auto font-['Manrope']">
            Tell us about your wedding, prom, photo shoot, or large-group event. Fill in what you know — we can refine details once we connect.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl p-7 sm:p-9 space-y-6">
          {/* Type chips */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Event type</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { key: "wedding", label: "Wedding" },
                { key: "prom", label: "Prom" },
                { key: "photo", label: "Photo" },
                { key: "corporate", label: "Corporate" },
                { key: "party", label: "Party" },
                { key: "other", label: "Other" },
              ].map(t => (
                <button key={t.key} type="button" onClick={() => setForm(f => ({ ...f, type: t.key }))}
                  data-testid={`open-type-${t.key}`}
                  className={`py-2.5 rounded-xl text-xs font-semibold border transition-all ${
                    form.type === t.key
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 font-medium">Event name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              data-testid="open-form-name" placeholder="Sarah & Michael's Wedding"
              className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                data-testid="open-form-date" required
                className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Start time</label>
              <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 font-medium">Venue / Address</label>
            <input value={form.venue} onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
              placeholder="Where will we be?"
              className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
          </div>

          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Your contact info</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={form.lead_client.name} onChange={e => setLead("name", e.target.value)}
                placeholder="Your name *" data-testid="open-form-lead-name" required
                className="h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
              <input value={form.lead_client.phone} onChange={e => setLead("phone", e.target.value)}
                placeholder="Phone"
                className="h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
              <input type="email" value={form.lead_client.email} onChange={e => setLead("email", e.target.value)}
                placeholder="Email"
                className="h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
          </div>

          {/* Roster */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Who's getting styled? ({form.roster.length})</p>
              <button type="button" onClick={addRow}
                className="text-xs inline-flex items-center gap-1 px-3 h-8 rounded-full bg-slate-900 text-white hover:bg-slate-800">
                <Plus className="w-3 h-3" /> Add person
              </button>
            </div>
            <div className="space-y-2">
              {form.roster.length === 0 && (
                <p className="text-xs text-slate-400 italic">Add each person and what they'd like (updo, makeup, nails…). Doesn't have to be final.</p>
              )}
              {form.roster.map((row, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input value={row.name || ""} onChange={e => updateRoster(idx, "name", e.target.value)}
                    placeholder="Name" className="col-span-4 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                  <input value={row.service || ""} onChange={e => updateRoster(idx, "service", e.target.value)}
                    placeholder="Service (updo, makeup…)" className="col-span-5 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                  <input type="time" value={row.start_time || ""} onChange={e => updateRoster(idx, "start_time", e.target.value)}
                    className="col-span-2 h-10 px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
                  <button type="button" onClick={() => removeRow(idx)}
                    className="col-span-1 h-10 rounded-lg hover:bg-rose-50 flex items-center justify-center">
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Inspo link (Pinterest / IG)</label>
              <input value={form.inspo_link} onChange={e => setForm(f => ({ ...f, inspo_link: e.target.value }))}
                placeholder="https://…"
                className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Dress / theme color</label>
              <input value={form.dress_color} onChange={e => setForm(f => ({ ...f, dress_color: e.target.value }))}
                placeholder="Ivory, blush, navy…"
                className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600 font-medium">Allergies / sensitivities</label>
            <input value={form.allergies} onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))}
              placeholder="(none)"
              className="w-full mt-1 h-11 px-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300" />
          </div>

          <div>
            <label className="text-xs text-slate-600 font-medium">Anything else?</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value.slice(0, 2000) }))}
              rows={3} placeholder="Trial run requests, must-have looks, special touches…"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-rose-300 resize-none" />
          </div>

          {error && <p className="text-rose-600 text-sm font-medium" data-testid="open-form-error">{error}</p>}

          <button type="submit" disabled={submitting} data-testid="open-form-submit"
            className="w-full h-12 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Send className="w-4 h-4" />
            {submitting ? "Sending…" : `Send to ${businessName}`}
          </button>
          <p className="text-[11px] text-slate-400 text-center font-['Manrope']">
            Your info is sent only to the salon. No account needed.
          </p>
        </form>
      </div>
    </div>
  );
}
